import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { maskPhone } from "@/lib/phone";

/**
 * A phone input that never paints an EXISTING record's number in full.
 * While `value === lockedValue` (i.e. the field still holds the number that was
 * pre-filled from a lead/patient) it shows the masked number read-only with a
 * "Change" button; "Change" clears the field so a new number can be typed.
 * Numbers the user types themselves are shown as typed. The raw value stays in
 * the parent's form state, so submit/search keep working unchanged.
 *
 * `onChange` receives the string value (same shape as LeadSearchInput).
 */
export function PhoneLockButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded px-1.5 py-0.5 text-xs font-medium text-navy hover:bg-navy/5"
    >
      Change
    </button>
  );
}

export function usePhoneLock(value, lockedValue) {
  const [unlocked, setUnlocked] = useState(false);
  useEffect(() => { setUnlocked(false); }, [lockedValue]);
  const locked = !!lockedValue && value === lockedValue && !unlocked;
  return [locked, () => setUnlocked(true)];
}

export default function MaskedPhoneInput({ value, onChange, lockedValue, ...rest }) {
  const [locked, unlock] = usePhoneLock(value, lockedValue);
  if (locked) {
    return (
      <div className="relative">
        <Input value={maskPhone(value)} readOnly className="pr-16" {...rest} />
        <PhoneLockButton onClick={() => { unlock(); onChange(""); }} />
      </div>
    );
  }
  return <Input value={value} onChange={(e) => onChange(e.target.value)} {...rest} />;
}
